// TanStack Query hook for the debtor-detail Ledger History tab.
//
// The bundled debtor-detail endpoint no longer carries the full ledger:
// it is fetched lazily, per page, from a dedicated endpoint so a
// long-lived debtor's thousands of ledger rows never block the initial
// slide-over open. Mirrors the self-contained pattern of useSubsidyLedger.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type { reconciliation_DebtorLedgerResponse } from "@/api";

export const debtorLedgerKeys = {
  all: ["reconciliation", "debtor-ledger"] as const,
  // orgId is part of the key so a tenant switch can't serve another org's
  // cached ledger (frontend.md: tenant-scoped query keys MUST include the
  // active orgId). limit/offset are part of the key so each page is cached
  // separately — the hook passes them to the API, so omitting them would
  // collide pages. `all` flags the full-ledger request (useDebtorLedgerAll,
  // which hits `?all=1`): it changes the request semantics, so its cache
  // entries must never be shared with a normal paginated page that happens to
  // use the same limit/offset.
  detail: (
    orgId: string | null,
    debtorId: string | null,
    limit?: number,
    offset?: number,
    all?: boolean,
  ) =>
    [
      ...debtorLedgerKeys.all,
      "detail",
      orgId,
      debtorId,
      limit,
      offset,
      // Appended only for the full-ledger request so paginated keys keep their
      // exact shape; a `?all=1` fetch can never share a paginated page's entry.
      ...(all ? [{ all: 1 }] : []),
    ] as const,
};

export interface UseDebtorLedgerParams {
  limit?: number;
  offset?: number;
}

export function useDebtorLedger(
  debtorId: string | null,
  params: UseDebtorLedgerParams = {},
) {
  const { orgId } = useAuth();
  const enabled = debtorId !== null && debtorId !== "";
  return useQuery<reconciliation_DebtorLedgerResponse>({
    queryKey: debtorLedgerKeys.detail(
      orgId ?? null,
      debtorId,
      params.limit,
      params.offset,
    ),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsLedger(
        debtorId as string,
        params.limit,
        params.offset,
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    // The account statement is money the operator acts on, so it opts in to
    // focus refetch over the app-wide `refetchOnWindowFocus: false` default —
    // see the FRESHNESS POLICY note in useDebtorAccountsList.ts.
    refetchOnWindowFocus: true,
  });
}
